import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import React, { useState } from "react";
import { GoPerson } from "react-icons/go";
import { RiLockPasswordLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api1 } from "../../../utils/axiosInstance";
import { GoogleLogin } from "@react-oauth/google";
import { login } from "../../../redux/authSlice";
import { useDispatch } from "react-redux";
import { API_ENDPOINTS } from "../../../services/apiEndpoints";

const MentorSignUpForm = () => {
  const [user, setUser] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [termsError, setTermsError] = useState("");

  const isPasswordValid = (password) => {
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);

    return (
      password.length >= minLength &&
      hasUppercase &&
      hasLowercase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const passwordMatch = user.password === user.confirmPassword;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const submitData = async (e) => {
    e.preventDefault();
    setTermsError(""); // Clear any existing error

    if (!passwordMatch) {
      toast.error("Password and confirm password do not match");
      return;
    }

    if (!isPasswordValid(user.password)) {
        toast.error("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
        return;
    }

    if (!isChecked) {
      setTermsError("You must accept the terms to proceed.");
      return;
    }

    const type = localStorage.getItem("userType");
    const userData = { ...user, type };

    try {
      const res = await api1.post(API_ENDPOINTS.SIGNUP, userData);
      localStorage.setItem("userEmail", res.data.email);
      if (!localStorage.getItem("firstName")) {
        localStorage.setItem("firstName", res.data.firstName);
      }
      if (!localStorage.getItem("lastName")) {
        localStorage.setItem("lastName", res.data.lastName);
      }
      navigate("/auth/otp", { state: { from: "/auth/signup" } });
    } catch (error) {
      console.error(error);
    }
  };

  const updateUserData = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSuccess = async (credentialResponse) => {
    try {
      const tokenId = credentialResponse.credential;
      const response = await api1.post(API_ENDPOINTS.GOOGLE_LOGIN, {
        tokenId,
        type: localStorage.getItem("userType"),
      });

      localStorage.setItem("authToken", response.data.token);
      localStorage.setItem("userType", response.data.type);
      localStorage.setItem("firstName", response.data.firstName);
      localStorage.setItem("lastName", response.data.lastName);
      localStorage.setItem("refreshToken", response.data.refreshToken);
      dispatch(login(response.data));

      window.dispatchEvent(new Event("accessTokenChanged"));

      setTimeout(() => {
        if (response.data.isProfileComplete) {
          window.location.href = "/dashboard/home";
        } else {
          window.location.href = "/dashboard/profile";
        }
      }, 100);
    } catch (error) {
      console.error("Google login failed", error);
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      <h1 className="text-3xl font-bold text-purple-800 mb-2">Create your account</h1>
      <p className="text-gray-500 mb-6 text-center">
        Welcome to OnTrack Careers. Let's get started by creating your account.
      </p>

      <form className="w-full max-w-md space-y-4">
        {/* First Name */}
        <InputField name="firstName" placeholder="Enter your first name" icon={<GoPerson />} onChange={updateUserData} />

        {/* Last Name */}
        <InputField name="lastName" placeholder="Enter your last name" icon={<GoPerson />} onChange={updateUserData} />

        {/* Email */}
        <InputField name="email" type="email" placeholder="Enter your email" icon={<GoPerson />} onChange={updateUserData} />

        {/* Password */}
        <PasswordField
          name="password"
          placeholder="Enter your password"
          show={showPassword}
          toggle={() => setShowPassword(!showPassword)}
          onChange={updateUserData}
        />

        {/* Confirm Password */}
        <PasswordField
          name="confirmPassword"
          placeholder="Confirm your password"
          show={showConfirmPassword}
          toggle={() => setShowConfirmPassword(!showConfirmPassword)}
          onChange={updateUserData}
        />

        {/* Terms Description */}
        <div className="text-sm text-gray-600 leading-relaxed space-y-2 mt-2">
          <p>
            By subscribing to OnTrack Careers, you confirm that you have{" "}
            <span className="font-semibold">read, understood, and agreed</span> to our{" "}
            <span className="font-semibold">Terms of Service</span> and{" "}
            <span className="font-semibold">Privacy Policy</span>. These documents outline your rights and responsibilities, how we handle your data, and the rules governing your use of our platform.
          </p>

          <p className="font-medium">We encourage you to review them carefully:</p>
          <ul className="list-disc list-inside text-purple-600 ml-2 space-y-1">
            <li>
              <a
                href="/termsOfService"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Terms of Service
              </a>
            </li>
            <li>
              <a
                href="/privacyPolicy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                View Privacy Policy
              </a>
            </li>
          </ul>
        </div>

        {/* Checkbox */}
        <div className="flex items-start gap-2 mt-3">
          <input
            type="checkbox"
            id="terms"
            checked={isChecked}
            onChange={(e) => {
              setIsChecked(e.target.checked);
              setTermsError("");
            }}
            className="mt-1"
          />
          <label htmlFor="terms" className="text-sm text-gray-700 leading-snug">
            By proceeding, you acknowledge and accept these policies in full.
          </label>
        </div>

        {/* Show error if checkbox not selected */}
        {termsError && (
          <p className="text-red-500 text-xs mt-1 ml-6">{termsError}</p>
        )}

        {/* Submit Button */}
        <button
          type="button"
          onClick={submitData}
          className="w-full py-3 text-white bg-custom-gradient rounded-lg shadow-md hover:opacity-90 transition-all"
        >
          Sign Up
        </button>

        {/* OR divider */}
        <div className="flex items-center my-4">
          <hr className="flex-grow border-gray-300" />
          <p className="text-gray-500 px-4">Or</p>
          <hr className="flex-grow border-gray-300" />
        </div>

        {/* Google Login */}
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => console.error("Google login failed")}
            theme="outline"
            size="large"
            shape="rectangular"
            width="100%"
          />
        </div>
      </form>

      {/* Login redirect */}
      <p className="text-sm text-gray-500 mt-4">
        Already have an account?{" "}
        <span
          onClick={() => navigate("/auth/login")}
          className="text-purple-600 cursor-pointer hover:underline"
        >
          Login
        </span>
      </p>
    </div>
  );
};

// Reusable InputField Component
const InputField = ({ name, placeholder, icon, type = "text", onChange }) => (
  <div>
    <div className="flex items-center border border-gray-300 rounded px-3 py-2">
      {icon}
      <input
        name={name}
        type={type}
        placeholder={placeholder}
        className="w-full outline-none text-gray-700 placeholder:text-sm ml-2"
        onChange={onChange}
      />
    </div>
  </div>
);

// Reusable PasswordField Component
const PasswordField = ({ name, placeholder, show, toggle, onChange }) => (
  <div className="relative">
    <div className="flex items-center border border-gray-300 rounded px-3 py-2">
      <RiLockPasswordLine className="mr-2 text-gray-500" />
      <input
        name={name}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        className="w-full outline-none text-gray-700 placeholder:text-sm"
        onChange={onChange}
      />
      <div
        className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
        onClick={toggle}
      >
        {show ? (
          <EyeOffIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <EyeIcon className="w-5 h-5 text-gray-500" />
        )}
      </div>
    </div>
  </div>
);

export default MentorSignUpForm;
